import { type AxiosInstance } from 'axios'

interface PaginatedTrackerParams {
  filters: ProgressTrackerFilterParams | null
  page: number
  page_size: number
  sort_by_submission_date?: 'desc' | 'asc'
}

interface PaginatedDataEntryParams extends PaginatedTrackerParams {
  sort_by_data_owner_name?: 'desc' | 'asc'
  sort_by_total_missing_data_points?: 'desc' | 'asc'
}

interface PaginatedDataEntryOwnerParams extends PaginatedTrackerParams {
  sort_by_asset_name?: 'asc' | 'desc'
  sort_by_category_name?: 'asc' | 'desc'
  sort_by_missing_data_points?: 'asc' | 'desc'
  sort_by_expected_data_points?: 'asc' | 'desc'
  sort_by_period_missing_data_points?: 'asc' | 'desc'
}

interface PaginatedApprovalParams extends PaginatedTrackerParams {
  sort_by_approver_name?: 'desc' | 'asc'
}

interface PaginatedRejectedSubmissionsParams extends PaginatedTrackerParams {
  sort_by_data_owner_name?: 'desc' | 'asc'
}

interface ProgressTrackerBulkNotifyParams {
  filters: ProgressTrackerFilterParams | null
  action: 'pending_approvals' | 'rejected_submissions'
  redirect_link: string
  submission_link: string
}

interface PendingEntriesPayload {
  redirect_link: string
}

interface PendingEntriesEmailPayload extends PendingEntriesPayload {
  assets?: string
  categories?: string
  start_date?: string
  end_date?: string
}

interface PendingEntriesPeriodEmailPayload extends PendingEntriesPayload {
  asset_id: number
  category_id: number
  missing_period: string
}

export default (axios: AxiosInstance) => ({
  listDatesAndCategories(companyId: number) {
    return axios.get(`/v1/progresstracker/company/${companyId}/assigned-categories-and-dates/`)
  },
  listAssets(companyId: number) {
    return axios.get<AssetAssignmentState[]>(`/v1/progresstracker/company/${companyId}/assigned-and-unassigned-assets/`)
  },
  pendingEntries(companyId: number, params: PaginatedDataEntryParams) {
    const { page, page_size, sort_by_data_owner_name, sort_by_total_missing_data_points } = params

    return axios.get(`/v1/progresstracker/company/${companyId}/pending-entries/`, {
      params: {
        ...params.filters,
        page,
        page_size,
        sort_by_data_owner_name,
        sort_by_total_missing_data_points,
      },
    })
  },
  ownerPendingEntries(companyId: number, dataOwnerId: number, params: PaginatedDataEntryOwnerParams) {
    const { page, page_size, sort_by_asset_name, sort_by_category_name, sort_by_expected_data_points, sort_by_missing_data_points, sort_by_period_missing_data_points } = params

    return axios.get(`/v1/progresstracker/company/${companyId}/pending-entries/data-owner/${dataOwnerId}/`, {
      params: {
        ...params.filters,
        page,
        page_size,
        sort_by_asset_name,
        sort_by_category_name,
        sort_by_expected_data_points,
        sort_by_missing_data_points,
        sort_by_period_missing_data_points,
      },
    })
  },
  pendingApprovals(companyId: number, params: PaginatedApprovalParams) {
    const { page, page_size, sort_by_approver_name, sort_by_submission_date } = params

    return axios.get(`/v1/progresstracker/company/${companyId}/pending-approvals-and-rejected-submissions/`, {
      params: {
        ...params.filters,
        action: 'pending_approvals',
        page,
        page_size,
        sort_by_approver_name,
        sort_by_submission_date,
      },
    })
  },
  rejectedSubmissions(companyId: number, params: PaginatedRejectedSubmissionsParams) {
    const { page, page_size, sort_by_data_owner_name, sort_by_submission_date } = params

    return axios.get(`/v1/progresstracker/company/${companyId}/pending-approvals-and-rejected-submissions/`, {
      params: {
        ...params.filters,
        action: 'rejected_submissions',
        page,
        page_size,
        sort_by_data_owner_name,
        sort_by_submission_date,
      },
    })
  },
  sendEmailReminder(companyId: number, params: TrackerEmailReminderParams) {
    return axios.post(`/v1/progresstracker/company/${companyId}/single-email-pending-rejected-submission/`, {}, { params })
  },
  sendPendingEntriesEmailReminder(companyId: number, dataOwnerId: number, payload: PendingEntriesEmailPayload | PendingEntriesPeriodEmailPayload) {
    return axios.post(`/v1/progresstracker/company/${companyId}/pending-entries/data-owner/${dataOwnerId}/send-email/`, payload)
  },
  bulkNotifyPendingEntries(companyId: number, payload: PendingEntriesEmailPayload) {
    return axios.post(`/v1/progresstracker/company/${companyId}/pending-entries/send-bulk-email/`, payload)
  },
  bulkNotify(companyId: number, params: ProgressTrackerBulkNotifyParams) {
    const { action, redirect_link, submission_link } = params

    return axios.post(`/v1/progresstracker/company/${companyId}/bulk-email-pending-rejected-submissions/`, {}, {
      params: {
        ...params.filters,
        action,
        redirect_link,
        submission_link,
      },
    })
  },
})
